import Cookies from 'js-cookie';
import { DEVICE_ID_COOKIE } from 'constants/cookies';
import { isDevelopment } from 'utils/environment';
import { getStringFromRequestCookie } from 'utils/cookies';
import { IncomingMessage, ServerResponse } from 'http';
import cookie from 'cookie';
import { SET_COOKIE_HEADER_NAME } from 'constants/headers';
import { isClientSide } from './host-config';

/**
 * Client-side cookie options for device ID storage
 * - expires: 365 days (1 year)
 * - secure: true in production, false in development
 * - sameSite: 'lax' for CSRF protection while allowing cross-site navigation
 */
const COOKIE_OPTIONS = {
  expires: 365, // 1 year
  secure: !isDevelopment,
  sameSite: 'lax' as const,
};

/**
 * Server-side cookie options for device ID storage
 * - httpOnly: false (allows client-side access)
 * - secure: true in production, false in development
 * - maxAge: 1 year in seconds
 * - sameSite: 'lax' for CSRF protection while allowing cross-site navigation
 */
const SERVER_COOKIE_OPTIONS = {
  httpOnly: false,
  secure: !isDevelopment,
  maxAge: 365 * 24 * 60 * 60, // 1 year (in seconds)
  sameSite: 'lax' as const,
};


/**
 * Retrieves or creates a device ID from server-side request cookies.
 *
 * This function is designed for use in Next.js server-side contexts (getServerSideProps,
 * API routes, middleware) where you have access to the incoming request and response objects.
 *
 * @param request The incoming HTTP request object (optional)
 * @param response  The server response object (optional, required for setting new cookies)
 * @returns A device ID string (existing from cookie or newly generated)
 *
 * @example
 * ```typescript
 * // In getServerSideProps
 * export async function getServerSideProps({ req, res }) {
 *   const deviceId = getDeviceIdFromRequest(req, res);
 *   // Device ID is now available and cookie is set if needed
 *   return { props: { deviceId } };
 * }
 *
 * // In API route
 * export default function handler(req, res) {
 *   const deviceId = getDeviceIdFromRequest(req, res);
 *   res.json({ deviceId });
 * }
 * ```
 *
 * @remarks
 * - If a device ID exists in the request cookies, it returns that value
 * - If no device ID exists, generates a new one using `Date.now().toString()`
 * - When a new device ID is created, it sets the cookie in the response headers
 * - The device ID cookie expires after 1 year
 * - Cookie security settings depend on the environment (secure in production)
 */
export const getDeviceIdFromRequest = (request: IncomingMessage, response: ServerResponse<IncomingMessage>): string => {
  let deviceId: string;
  deviceId = getStringFromRequestCookie(DEVICE_ID_COOKIE, request);
  if (!deviceId) {
    deviceId = Date.now().toString();
    response.setHeader(
      SET_COOKIE_HEADER_NAME,
      cookie.serialize(DEVICE_ID_COOKIE, deviceId, SERVER_COOKIE_OPTIONS)
    );
  }
  return deviceId;
};

/**
 * Retrieves or creates a device ID from client-side cookies.
 *
 * This function is designed for use in client-side React components, hooks, and utilities
 * where you need to access the device ID in the browser environment.
 *
 * @returns A device ID string (existing from cookie or newly generated)
 *
 * @example
 * ```typescript
 * // In a React component
 * function MyComponent() {
 *   const deviceId = getDeviceIdFromCookie();
 *   return <div>Device ID: {deviceId}</div>;
 * }
 *
 * // In a custom hook
 * function useDeviceId() {
 *   const [deviceId, setDeviceId] = useState<string>('');
 *
 *   useEffect(() => {
 *     setDeviceId(getDeviceIdFromCookie());
 *   }, []);
 *
 *   return deviceId;
 * }
 *
 * // In analytics tracking
 * function trackEvent(eventName: string) {
 *   const deviceId = getDeviceIdFromCookie();
 *   analytics.track(eventName, { deviceId });
 * }
 * ```
 *
 * @remarks
 * - Only works in client-side environments (browser)
 * - If a device ID exists in cookies, it returns that value
 * - If no device ID exists, generates a new one using `Date.now().toString()`
 * - When a new device ID is created, it sets the cookie using js-cookie
 * - The device ID cookie expires after 1 year
 * - Cookie security settings depend on the environment (secure in production)
 * - Returns undefined if called on the server-side
 */
export const getDeviceIdFromCookie = (): string => {
  let deviceId: string;

  if (isClientSide()) {
    deviceId = Cookies.get(DEVICE_ID_COOKIE);
    if (!deviceId) {
      deviceId = Date.now().toString();
      Cookies.set(DEVICE_ID_COOKIE, deviceId, COOKIE_OPTIONS);
    }
  }

  return deviceId;
};